import TrendyStatistic from "./TrendyStatistic.js";
import Statistic from "./Statistic.js";
import {TimeDelta} from "../time.js";
import {isEmpty} from "../utils.js";


export class KPI {

    // Data Types
    constructor({
                    title,
                    previousWindow,
                    currentWindow,
                    operation = Statistic.AVERAGE,
                    dataType = Statistic.NUMBER,
                    chartType = "line"
                }) {

        if (isEmpty(previousWindow?.start || previousWindow?.end)) {
            console.error("Previous window: ", previousWindow);
            throw new Error("Previous window must be defined for KPI");
        }
        if (isEmpty(currentWindow?.start) || isEmpty(currentWindow?.end)) {
            console.error("Current window: ", currentWindow);
            throw new Error("Current window must be defined for KPI");
        }

        this.title = title;
        this.chartType = chartType;
        this.previousWindow = previousWindow;
        this.currentWindow = currentWindow;
        this.statistic = new TrendyStatistic({
            title: title,
            operation: operation,
            dataType: dataType,
            currentWindow,
            previousWindow
        }); // This is a subset of universe; e.g. transactions for one product vs all products.
    }

    add({value, date, isSample = true}) {
        this.statistic.add({value, date, isSample});
    }

    value() {
        return this.statistic.value();
    }

    displayValue() {
        return this.statistic.displayValue();
    }

    chartDatasets({binSizeDays}) {
        return this.statistic.chartDatasets({binSizeDays});
    }

    trend() {
        return this.statistic.trend();
    }

    chartPlugins(extraData) {
        return {
            tooltip: {
                callbacks: {
                    label: (context) => {
                        if (context.dataset.period === "previous") {
                            const date = extraData?.alignedPrevious[context.dataIndex];
                            if (date) return context.formattedValue + " on " + TimeDelta.formatShortDate(date);
                            return context.formattedValue;
                        } else if (context.dataset.period === "current") {
                            const date = extraData?.alignedCurrent[context.dataIndex];
                            if (date) return context.formattedValue + " on " + TimeDelta.formatShortDate(date);
                            return context.formattedValue;
                        }
                    },
                    title: (context) => {
                        context = context[0];
                        return extraData?.title;
                    }
                }
            },
            legend: {
                display: true,
                position: "top",
            },
            title: {
                display: true,
                text: this.title
            }
        }
    }


    getYAxisType(datasets) {
        const globalDatasets = datasets.filter(dataset => dataset.label.slice(0, 6) === "Global");
        const sampleDatasets = datasets.filter(dataset => dataset.label.slice(0, 6) !== "Global");
        const getAbsPointValue = (point) => {
            if (!point) {
                return 0;
            }
            if (Array.isArray(point)) {
                return Math.abs(point[1]);
            } else if (typeof point === "object" && point !== null) {
                return Math.abs(point.y) || 0; // Assuming point has a 'y' property
            } else {
                return Math.abs(point);
            }
        }

        if (globalDatasets && sampleDatasets) {
            const globalMax = Math.max(...globalDatasets.flatMap(dataset => dataset.data.map(d => getAbsPointValue(d))));
            const sampleMax = Math.max(...sampleDatasets.flatMap(dataset => dataset.data.map(d => getAbsPointValue(d))));
            if (globalMax > 100 * sampleMax || sampleMax > 100 * globalMax) {
                return "logarithmic"; // Use logarithmic scale for better visibility
            }
        }
        return "linear";
    }

    chartOptions(labels, datasets, extraData) {
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: this.chartPlugins(extraData),
            scales: {
                x: {
                    type: "category",
                    title: {
                        display: true,
                        text: "Date"
                    },
                    ticks: {
                        callback: (value, index, ticks, foo) => {
                            return labels[index];
                        },
                        autoSkip: true,
                        maxRotation: 45,
                        minRotation: 45,
                    },
                    min: 0,
                    max: this.statistic.dailyStatistics.dailyStats.length || undefined
                },
                y: {
                    type: this.getYAxisType(datasets),
                    title: {
                        display: true,
                        text: this.title
                    }
                }
            }
        };
    }

    chart({binSizeDays}) {
        const {labels, datasets, extraData} = this.chartDatasets({binSizeDays})
        return {
            type: this.chartType,
            options: this.chartOptions(labels, datasets, extraData),
            data: {labels: labels, datasets: datasets}
        }
    }

    chartKey() {
        return "kpi-chart-" + this.title;
    }
}


export default KPI;